import type { ChannelCloseoutOperationStatus, ChannelCloseoutSyncStatus } from '@/shared/api';
import { nightsLabel } from './calendar/calendarFormat';
import { nightsInRange } from './calendar/rangePickerModel';
import { monthLabel } from './channelCloseoutTimelineModel';

/** Die zwei Kanäle, die eine Kanalregel schließen kann; andere nimmt der Server nicht an. */
export const CLOSEOUT_CHANNELS = [
  { code: 'BookingCom', label: 'Booking.com' },
  { code: 'Airbnb', label: 'Airbnb' },
] as const;

export function channelLabel(code: string) {
  return CLOSEOUT_CHANNELS.find((channel) => channel.code === code)?.label ?? code;
}

export function closeoutStatusLabel(status: ChannelCloseoutSyncStatus) {
  return {
    Pending: 'Wird übertragen',
    Synced: 'Bestätigt',
    NeedsReconcile: 'Abgleich nötig',
    Drifted: 'Abweichung erkannt',
    Failed: 'Fehlgeschlagen',
  }[status];
}

export function operationStatusLabel(status: ChannelCloseoutOperationStatus) {
  return {
    Pending: 'Vorgemerkt',
    Claimed: 'In Bearbeitung',
    InFlight: 'Versendet, Bestätigung offen',
    NeedsReconcile: 'Abgleich nötig',
    Confirmed: 'Bestätigt',
    Failed: 'Fehlgeschlagen',
    Superseded: 'Überholt',
  }[status];
}

export function closeoutStatusClass(status: ChannelCloseoutSyncStatus) {
  if (status === 'Synced') return 'bg-forest-soft text-forest-deep';
  if (status === 'Failed') return 'bg-sealwax-tint text-sealwax';
  return 'bg-amber-tint text-amber-ink';
}

/** Ein Fehlercode ist für Entwickler; der Betrieb braucht den Satz dazu. */
const ERROR_TEXT: Record<string, string> = {
  channel_catalog_stale: 'Wartet auf eine aktuelle Kanalliste von Channex.',
  closeout_targets_unavailable:
    'Ein Apartment oder Kanal ist nicht mehr zugeordnet. Regel anpassen oder aufheben.',
  create_outcome_unknown:
    'Unklar, ob die Regel bei Channex steht. Sie wird geprüft, nicht blind erneut gesendet.',
  update_outcome_unknown:
    'Unklar, ob Channex die Änderung erhalten hat. Sie wird geprüft und bei Bedarf erneut gesendet.',
  delete_outcome_unknown:
    'Unklar, ob Channex die Regel entfernt hat. Sie wird geprüft und bei Bedarf erneut entfernt.',
  delete_readback_present:
    'Die Regel stand nach dem Entfernen noch bei Channex. Sie wird erneut entfernt.',
  duplicate_remote_rules: 'Die Regel steht bei Channex doppelt. Bitte dort bereinigen.',
  remote_marker_mismatch: 'Bei Channex steht eine abweichende Fassung dieser Regel.',
  rule_readback_mismatch: 'Channex hat die Regel anders gespeichert als gesendet.',
  remote_rule_drift: 'Die Regel wurde bei Channex geändert und wird korrigiert.',
  remote_rule_missing:
    'Die Regel fehlt bei Channex. Bestätigt sich das, lässt sie sich neu senden.',
  rule_catalog_incomplete: 'Channex hat die Regelliste nur unvollständig geliefert.',
  rule_catalog_unavailable: 'Channex war beim Abgleich nicht erreichbar.',
  update_rejected: 'Channex hat die Änderung abgelehnt.',
  delete_rejected: 'Channex hat das Aufheben abgelehnt.',
  update_unconfirmed: 'Channex hat die Änderung auch nach mehreren Versuchen nicht bestätigt.',
  delete_unconfirmed: 'Channex hat das Entfernen auch nach mehreren Versuchen nicht bestätigt.',
  workspace_inactive: 'Der Workspace ist inaktiv. Die Regel wurde nicht gesendet.',
};

/** Abgeschlossen heißt derselbe Code: Die Regel fehlt belegt bei Channex; erst die Bestätigung sendet neu. */
export function closeoutErrorText(code: string | null, failed = false) {
  if (!code) return null;
  if (failed && code === 'create_outcome_unknown') {
    return 'Die Regel steht nicht bei Channex. „Abwesenheit bestätigen“ sendet sie neu.';
  }
  return ERROR_TEXT[code] ?? `Fehler ${code}`;
}

/** Was der Server erneut senden lässt: Nichts davon kann eine zweite Regel bei Channex anlegen. */
export const RETRYABLE_CLOSEOUT_ERRORS: readonly string[] = [
  'closeout_targets_unavailable',
  'update_rejected',
  'delete_rejected',
  'update_unconfirmed',
  'delete_unconfirmed',
];

const day = (iso: string) => new Date(`${iso}T00:00:00Z`);

/** Wie im Zeitraumkalender: jeder geschlossene Tag ist eine nicht buchbare Nacht. */
export function closeoutNightsLabel(start: string, end: string) {
  return nightsLabel(nightsInRange({ from: start, to: end }));
}

function formatDay(iso: string) {
  return new Intl.DateTimeFormat('de-DE', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(day(iso));
}

export function formatCloseoutDateRange(start: string, end: string) {
  return `${formatDay(start)} – ${formatDay(end)}`;
}

/** „A, B und C" */
export function listGerman(items: readonly string[]) {
  return items.length < 2
    ? (items[0] ?? '')
    : `${items.slice(0, -1).join(', ')} und ${items.at(-1)}`;
}

function channelLabels(channels: readonly string[], selected: boolean) {
  return CLOSEOUT_CHANNELS.filter((channel) => channels.includes(channel.code) === selected).map(
    (channel) => channel.label,
  );
}

/** „Booking.com zu · Okt 26 – Jun 27", im selben Monat „… · Okt 26": ein überschreibbarer Vorschlag. */
export function suggestCloseoutName(channels: string[], start: string, end: string) {
  if (channels.length === 0 || !start || !end) return '';
  const [from, to] = [monthLabel(start), monthLabel(end)];
  return `${listGerman(channelLabels(channels, true))} zu · ${from === to ? from : `${from} – ${to}`}`;
}

/**
 * Die Regel in einem Satz: was schließt und was offen bleibt. Kanalregel und MinStay sehen im
 * Kalender ähnlich aus; der Satz hält sie auseinander.
 */
export function closeoutSentence(
  channels: readonly string[],
  apartments: readonly string[],
  start: string,
  end: string,
) {
  const closed = channelLabels(channels, true);
  const open = [...channelLabels(channels, false), 'Direktbuchungen'];
  const target = apartments.length > 3 ? `${apartments.length} Apartments` : listGerman(apartments);
  return (
    `${listGerman(closed)} ${closed.length > 1 ? 'werden' : 'wird'} für ${target} vom ` +
    `${formatDay(start)} bis ${formatDay(end)} geschlossen (${closeoutNightsLabel(start, end)}). ` +
    `${listGerman(open)} bleiben offen, Preise und MinStay ändern sich nicht.`
  );
}
